import { useState } from 'react';
import { REGION_LIST } from '@constants/limits';
import { LOCATION_FILTERS as F, VILLAGE_KIND } from '@locales/id/locations';
import type { District, Village } from '@lib/api';
import { Combobox } from '@ui';
import { useDistricts, useVillages, type AreaListState } from '@features/locations/model/useAreaLists';

interface Common {
  id?: string;
  describedBy?: string;
  invalid?: boolean;
  /** the level above is not chosen yet: the box says what to choose first */
  disabled?: boolean;
  disabledText: string;
  value: { code: string; name: string } | null;
}

/**
 * One district (kecamatan) of a regency, or one village (desa / kelurahan) of a district, searched by name: the list
 * is a page of its parent, fetched only while the box is open (the ±84 thousand villages are never listed).
 */
function AreaCombobox<T extends { code: string; name: string }>({
  id,
  describedBy,
  invalid,
  disabled,
  disabledText,
  value,
  placeholder,
  list,
  setOpen,
  q,
  setQ,
  none,
  sub,
  onChange,
}: Common & {
  placeholder: string;
  list: AreaListState<T>;
  setOpen: (o: boolean) => void;
  q: string;
  setQ: (q: string) => void;
  none: string;
  sub?: (x: T) => string;
  onChange: (x: T) => void;
}) {
  const { items } = list;
  return (
    <Combobox<string>
      id={id}
      describedBy={describedBy}
      invalid={invalid}
      disabled={disabled}
      placeholder={disabled ? disabledText : placeholder}
      selected={value?.name ?? ''}
      query={q}
      onQuery={setQ}
      options={items.map((x) => ({ value: x.code, title: x.name, sub: sub?.(x) ?? x.code }))}
      onPick={(o) => {
        const x = items.find((i) => i.code === o.value);
        if (x) onChange(x);
      }}
      loading={list.state.loading}
      error={list.state.error}
      onRetry={list.state.retry}
      empty={list.partial ? F.areaTypeMore(REGION_LIST.searchMin) : q.trim() ? none : F.areaNone}
      footer={list.partial ? F.areaTypeMore(REGION_LIST.searchMin) : undefined}
      onOpen={(o) => {
        setOpen(o);
        if (o) setQ('');
      }}
    />
  );
}

export function DistrictPicker({ regencyCode, onChange, ...rest }: Common & { regencyCode: string; onChange: (d: District) => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const list = useDistricts(regencyCode, q, open);
  return <AreaCombobox<District> {...rest} placeholder={F.districtSearch} list={list} setOpen={setOpen} q={q} setQ={setQ} none={F.noDistrict} onChange={onChange} />;
}

export function VillagePicker({ districtCode, onChange, ...rest }: Common & { districtCode: string; onChange: (v: Village) => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const list = useVillages(districtCode, q, open);
  return (
    <AreaCombobox<Village>
      {...rest}
      placeholder={F.villageSearch}
      list={list}
      setOpen={setOpen}
      q={q}
      setQ={setQ}
      none={F.noVillage}
      sub={(v) => `${VILLAGE_KIND[v.kind]} · ${v.code}`}
      onChange={onChange}
    />
  );
}
